<script setup lang="ts">
/**
 * The account, at the navbar's right end, as Tencent Cloud's is: the name
 * over the role beside the avatar, and behind them a panel — who is signed
 * in and as what, what waits on staff, the console's own settings,
 * 退出登录. The numbers are read each time the panel opens.
 */
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { IconLock, IconSettings, IconFile, IconSafe, IconExport } from '@arco-design/web-vue/es/icon'
import { useAuthStore } from '@/store/auth'
import { ticketSummary, refreshTickets } from '@/store/tickets'
import { attentionItems, loadDashSummary, type DashSummary } from '@/utils/dashboard'
import { navPanel } from '@/utils/layout'
import { isNarrow } from '@/utils/responsive'

const { t } = useI18n()
const router = useRouter()
const auth = useAuthStore()

// One panel out at a time (navPanel): 工单's goes when this one comes.
const open = computed(() => navPanel.value === 'account')
function onVisible(v: boolean) {
  if (v) navPanel.value = 'account'
  else if (open.value) navPanel.value = null
}
const dash = ref<DashSummary | null>(null)

const user = computed(() => auth.user)
const initial = computed(() => (user.value?.username ?? 'G').slice(0, 1).toUpperCase())
const role = computed(() =>
  user.value?.role === 'admin'
    ? { color: 'green', label: t('navbar.roleAdmin') }
    : { color: 'arcoblue', label: t('navbar.roleOperator') },
)

async function load() {
  const [d] = await Promise.allSettled([loadDashSummary(), refreshTickets()])
  if (d.status === 'fulfilled') dash.value = d.value
}
watch(open, (v) => {
  if (v) void load()
})

// What waits on staff, each to the page that settles it.
const stats = computed(() => [
  { key: 'lostNodes', n: dash.value?.nodes.lost ?? 0, route: 'nodes' },
  { key: 'openTickets', n: (ticketSummary.value?.open ?? 0) + (ticketSummary.value?.processing ?? 0), route: 'tickets' },
  { key: 'failedTasks', n: dash.value?.tasks.failed ?? 0, route: 'tasks' },
  { key: 'unread', n: attentionItems(dash.value).length, route: 'dashboard' },
])

const links = [
  { route: 'account-security', label: 'securityCenter.title', icon: IconLock },
  { route: 'system-settings', label: 'systemSettings.title', icon: IconSettings },
  { route: 'audit', label: 'audit.title', icon: IconFile },
  { route: 'license', label: 'license.title', icon: IconSafe },
]

function go(name: string) {
  navPanel.value = null
  router.push({ name })
}
function logout() {
  navPanel.value = null
  auth.logout()
  // The guard sends a signed-out page to 登录 only on the next navigation.
  void router.replace({ name: 'login' })
}
</script>

<template>
  <!-- Opens under the pointer, as Tencent Cloud's does; a phone has no
       pointer, so a tap. The panel's top is the bar's rule: the cell is the
       bar's full height, 1px above it. -->
  <a-trigger
    :popup-visible="open"
    :trigger="isNarrow ? 'click' : 'hover'"
    position="br"
    :popup-offset="1"
    :mouse-leave-delay="200"
    @popup-visible-change="onVisible"
  >
    <button type="button" class="account" :class="{ bare: isNarrow, open }">
      <span v-if="!isNarrow" class="account-text">
        <span class="account-name">{{ user?.username }}</span>
        <span class="account-sub">{{ role.label }}</span>
      </span>
      <a-avatar :size="32" :style="{ backgroundColor: 'rgb(var(--primary-6))' }">{{ initial }}</a-avatar>
    </button>
    <template #content>
      <div class="nav-panel at-edge">
        <div class="head">
          <div class="who">
            <span class="who-name">{{ user?.username }}</span>
            <a-tag :color="role.color" size="small">{{ role.label }}</a-tag>
          </div>
          <div class="who-id">{{ user ? t('navbar.accountIdFull', { id: user.id }) : '' }}</div>
        </div>
        <div class="stats">
          <button v-for="s in stats" :key="s.key" type="button" class="stat" @click="go(s.route)">
            <span class="stat-n">{{ s.n }}</span>
            <span class="stat-label">{{ t(`navbar.${s.key}`) }}</span>
          </button>
        </div>
        <div class="nav-panel-group">
          <button v-for="l in links" :key="l.route" type="button" class="nav-panel-row" @click="go(l.route)">
            <component :is="l.icon" />{{ t(l.label) }}
          </button>
        </div>
        <div class="nav-panel-group">
          <button type="button" class="nav-panel-row" @click="logout">
            <IconExport />{{ t('app.logout') }}
          </button>
        </div>
      </div>
    </template>
  </a-trigger>
</template>

<style scoped>
/* The trigger: its name over its ID, right-aligned against the avatar —
   a cell the bar's full height, out to the window's edge (App.vue's
   .account-item), Arco's hover grey over all of it while the panel is out.
   A phone's is the avatar alone. */
.account {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 100%;
  padding: 0 var(--nav-edge, 20px) 0 16px;
  border: none;
  border-radius: 0;
  background: none;
  font: inherit;
  text-align: right;
  cursor: pointer;
  transition: background-color 0.1s;
}
.account:hover,
.account.open {
  background-color: var(--color-fill-2);
}
.account.bare {
  padding-left: 8px;
}
.account-text {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  max-width: 140px;
}
.account-name,
.account-sub {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.account-name {
  color: var(--color-text-1);
  font-size: 14px;
  line-height: 20px;
}
.account-sub {
  color: var(--color-text-2);
  font-size: 12px;
  line-height: 18px;
}

/* The panel is .nav-panel (base.css), flush with the window's edge; the
   head here is who is signed in, then what waits on them. */
.head {
  padding: 20px 20px 0;
}
.who {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.who-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--color-text-1);
  font-size: 18px;
  font-weight: 500;
  line-height: 26px;
}
.who .arco-tag {
  flex: none;
}
.who-id {
  margin-top: 6px;
  color: var(--color-text-2);
  font-size: 13px;
  line-height: 20px;
}
.head-wallet {
  margin-top: 16px;
}
.stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  padding: 12px 8px;
}
.stat {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 8px 12px;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.1s;
}
.stat:hover {
  background-color: var(--color-fill-2);
}
.stat-n {
  color: var(--color-text-1);
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
  font-variant-numeric: tabular-nums;
}
.stat-label {
  color: var(--color-text-3);
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
}
</style>
